import { useEffect, useState } from "react";
import { Loader2, Save, KeyRound, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Field } from "@/components/common";
import { UserPermissions } from "@/components/sistema/UserPermissions";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, maskPhone, digits, ROLE_LABELS, ROLE_HINTS, PAY_LABELS, isAdminRole, isManagerRole, activeMethods } from "@/lib/api";

function TicketPayments({ edit, setEdit }) {
  const { session } = useAuth();
  const methods = activeMethods(session);
  const list = edit.ticket_payments ?? methods;
  const toggle = (m, on) => setEdit((x) => ({ ...x, ticket_payments: on ? [...new Set([...list, m])] : list.filter((y) => y !== m) }));
  return (
    <div className="space-y-2" data-testid="user-ticket-payments">
      <p className="text-sm font-medium">Formas de pagamento liberadas na Venda ticket</p>
      <p className="text-xs text-muted-foreground">Aparecem só as formas ativas em Minha empresa → Formas de pagamento.</p>
      <div className="grid sm:grid-cols-2 gap-2">
        {methods.map((m) => <label key={m} className="flex items-center justify-between rounded-lg border px-3 py-2 text-sm">{PAY_LABELS[m]}<Switch checked={list.includes(m)} onCheckedChange={(v) => toggle(m, v)} data-testid={`user-ticket-pay-${m}`} /></label>)}
      </div>
    </div>
  );
}
import { toast } from "sonner";

const dsValue = (v) => (v == null ? "loja" : v ? "on" : "off");

function DirectSaleFields({ edit, setEdit }) {
  const { session } = useAuth();
  const store = !!session.tenant.settings?.mobile_direct_sale;
  return (
    <div className="space-y-3 rounded-lg border p-3" data-testid="user-direct-sale-box">
      <Field label="Venda Direta no app do celular" hint="Ligada: no celular o usuário só faz venda direta (ticket), sem comandas. O Modo de venda precisa incluir Venda ticket">
        <Select value={dsValue(edit.direct_sale)} onValueChange={(v) => setEdit((x) => ({ ...x, direct_sale: v === "loja" ? null : v === "on" }))}>
          <SelectTrigger data-testid="user-direct-sale-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="loja" data-testid="user-direct-sale-loja">Seguir a loja (hoje {store ? "ligada" : "desligada"})</SelectItem>
            <SelectItem value="on" data-testid="user-direct-sale-on">Ligada para este usuário</SelectItem>
            <SelectItem value="off" data-testid="user-direct-sale-off">Desligada para este usuário</SelectItem>
          </SelectContent>
        </Select>
      </Field>
      {isManagerRole(edit) ? <p className="text-xs text-muted-foreground">Gerente e proprietário mudam a Venda Direta da loja toda em Configurações do app.</p> : (
        <label className="flex items-center justify-between gap-3 text-sm">
          <span>Pode mudar no próprio app<span className="block text-xs text-muted-foreground">O usuário liga ou desliga a Venda Direta em Configurações do app; vale em qualquer celular em que ele entrar</span></span>
          <Switch checked={!!edit.direct_sale_self} onCheckedChange={(v) => setEdit((x) => ({ ...x, direct_sale_self: v }))} data-testid="user-direct-sale-self-switch" />
        </label>
      )}
    </div>
  );
}

const ROLE_OPTIONS = ["admin", "gerente", "caixa", "garcom", "cozinha", "entregador", "estacionamento"];
export const EMPTY_USER = { name: "", login: "", email: "", whatsapp: "", phone: "", password: "", password2: "", role: "caixa", active: true, max_discount_pct: "", permission_overrides: {}, screen_areas: null, report_access: null, store_access: null, waiter_view: false, direct_sale: null, direct_sale_self: false, partial_receipt: false };

function PassInput({ value, onChange, show, setShow, testId }) {
  const Icon = show ? EyeOff : Eye;
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} className="pr-10" autoComplete="new-password" data-testid={testId} />
      <button type="button" onClick={() => setShow(!show)} title={show ? "Ocultar senhas" : "Ver senhas"} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground" data-testid={`${testId}-toggle`}><Icon className="h-4 w-4" /></button>
    </div>
  );
}

function PassMatch({ a, b }) {
  if (!a || !b) return null;
  const ok = a === b;
  return <p className={`sm:col-span-2 -mt-2 text-xs font-medium ${ok ? "text-emerald-500" : "text-red-500"}`} data-testid="user-password-match">{ok ? "As senhas conferem" : "As senhas não conferem"}</p>;
}

function DataTab({ edit, setEdit, access }) {
  const [show, setShow] = useState(false);
  const set = (k, mask) => (e) => setEdit((x) => ({ ...x, [k]: mask ? mask(e.target.value) : e.target.value }));
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2 flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm" data-testid="user-access-number-info">
        <KeyRound className="h-5 w-5 text-primary shrink-0" />
        <span>Para entrar, o usuário informa o <b>número de acesso <span className="font-mono tracking-widest">{access}</span></b>, o login e a senha.</span>
      </div>
      <Field label="Nome completo" className="sm:col-span-2"><Input value={edit.name} onChange={set("name")} data-testid="user-name-input" /></Field>
      <Field label="Login" hint="Letras minúsculas, números, ponto ou hífen"><Input value={edit.login} disabled={!!edit.id} onChange={set("login", (v) => v.toLowerCase().replace(/\s/g, ""))} data-testid="user-login-input" /></Field>
      <Field label="E-mail para recuperação de senha"><Input type="email" value={edit.email || ""} onChange={set("email")} data-testid="user-email-input" /></Field>
      <Field label="WhatsApp"><Input value={maskPhone(edit.whatsapp || "")} onChange={set("whatsapp", digits)} data-testid="user-whatsapp-input" /></Field>
      <Field label="Telefone"><Input value={maskPhone(edit.phone || "")} onChange={set("phone", digits)} data-testid="user-phone-input" /></Field>
      <Field label={edit.id ? "Nova senha (opcional)" : "Senha"} hint="Mínimo 6 caracteres"><PassInput value={edit.password} onChange={set("password")} show={show} setShow={setShow} testId="user-password-input" /></Field>
      <Field label="Confirmar senha"><PassInput value={edit.password2} onChange={set("password2")} show={show} setShow={setShow} testId="user-password2-input" /></Field>
      <PassMatch a={edit.password} b={edit.password2} />
    </div>
  );
}

function AccessTab({ edit, setEdit, defaults, stores, users }) {
  const copyFrom = (id) => {
    const u = users.find((x) => x.id === id);
    if (!u) return;
    setEdit((x) => ({ ...x, role: u.role === "owner" ? "admin" : u.role, permission_overrides: { ...(u.permission_overrides || {}) }, screen_areas: u.screen_areas ?? null, report_access: u.report_access ?? null, store_access: u.store_access ?? null }));
    toast.success(`Permissões copiadas de ${u.name}`);
  };
  if (["owner", "admin"].includes(edit.role)) {
    return (<div className="space-y-3"><RoleSelect edit={edit} setEdit={setEdit} /><p className="text-sm text-muted-foreground">Administradores têm acesso completo a todas as telas, relatórios e empresas.</p></div>);
  }
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-4">
        <RoleSelect edit={edit} setEdit={setEdit} />
        <Field label="Copiar permissões de outro usuário">
          <Select value="" onValueChange={copyFrom}>
            <SelectTrigger data-testid="user-copy-perms-select"><SelectValue placeholder="Escolha um usuário" /></SelectTrigger>
            <SelectContent>{users.filter((u) => u.id !== edit.id).map((u) => <SelectItem key={u.id} value={u.id}>{u.name} ({ROLE_LABELS[u.role]})</SelectItem>)}</SelectContent>
          </Select>
        </Field>
      </div>
      <UserPermissions edit={edit} setEdit={setEdit} defaults={defaults} stores={stores} />
    </div>
  );
}

function RoleSelect({ edit, setEdit }) {
  return (
    <Field label="Nível de acesso" hint={ROLE_HINTS[edit.role === "owner" ? "admin" : edit.role]}>
      <Select value={edit.role} disabled={edit.role === "owner"} onValueChange={(v) => setEdit((x) => ({ ...x, role: v, permission_overrides: {}, screen_areas: null }))}>
        <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
        <SelectContent>{edit.role === "owner" && <SelectItem value="owner">Proprietário</SelectItem>}{ROLE_OPTIONS.map((r) => <SelectItem key={r} value={r} data-testid={`user-role-option-${r}`}>{ROLE_LABELS[r]}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

export function UserDialog({ user, users, stores, onClose, onSaved }) {
  const { session } = useAuth();
  const [edit, setEdit] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/users/roles").then((r) => setDefaults(r.data)).catch(() => {}); }, []);
  useEffect(() => { setEdit(user ? { ...EMPTY_USER, ...user, password: "", password2: "", max_discount_pct: user.max_discount_pct ?? "" } : null); }, [user]);
  const save = async () => {
    if (edit.password !== edit.password2) return toast.error("As senhas não conferem");
    setBusy(true);
    try {
      const body = { name: edit.name, login: edit.login, email: edit.email || "", whatsapp: edit.whatsapp || "", phone: edit.phone || "", password: edit.password || null, role: edit.role, active: edit.active,
        max_discount_pct: edit.max_discount_pct === "" || edit.max_discount_pct == null ? null : Number(edit.max_discount_pct), permissions: edit.permission_overrides || {},
        screen_areas: edit.screen_areas, report_access: edit.report_access, sale_mode: edit.sale_mode || "pdv", ticket_payments: edit.ticket_payments ?? null, waiter_view: !!edit.waiter_view, direct_sale: edit.direct_sale ?? null, direct_sale_self: !isManagerRole(edit) && !!edit.direct_sale_self, partial_receipt: !!edit.partial_receipt, store_access: edit.store_access && edit.store_access.length < stores.length ? edit.store_access : null };
      edit.id ? await api.put(`/users/${edit.id}`, body) : await api.post("/users", body);
      toast.success("Usuário salvo");
      onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!edit} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="user-modal">
        <DialogHeader><DialogTitle>{edit?.id ? `Editar usuário ${edit.name}` : "Novo usuário"}</DialogTitle></DialogHeader>
        {edit && (
          <Tabs defaultValue="dados">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="dados" data-testid="user-tab-dados">Dados do usuário</TabsTrigger>
              <TabsTrigger value="permissoes" data-testid="user-tab-permissoes">Permissões de acesso</TabsTrigger>
              <TabsTrigger value="config" data-testid="user-tab-config">Configurações</TabsTrigger>
            </TabsList>
            <TabsContent value="dados" className="mt-4"><DataTab edit={edit} setEdit={setEdit} access={session.tenant.access_number} /></TabsContent>
            <TabsContent value="permissoes" className="mt-4"><AccessTab edit={edit} setEdit={setEdit} defaults={defaults} stores={stores} users={users} /></TabsContent>
            <TabsContent value="config" className="mt-4 space-y-4">
              {edit.role !== "owner" ? <label className="flex items-center justify-between rounded-lg border px-3 py-3 text-sm">Usuário ativo (pode entrar no sistema)<Switch checked={edit.active} onCheckedChange={(v) => setEdit((x) => ({ ...x, active: v }))} data-testid="user-active-switch" /></label>
                : <p className="text-sm text-muted-foreground">O proprietário está sempre ativo.</p>}
              {!["owner", "admin"].includes(edit.role) && <Field label="Limite de desconto (%)" hint="Vazio = padrão da empresa"><Input type="number" value={edit.max_discount_pct} onChange={(e) => setEdit((x) => ({ ...x, max_discount_pct: e.target.value }))} data-testid="user-max-discount-input" /></Field>}
              <Field label="Modo de venda" hint="Venda ticket exige que o usuário abra o próprio caixa">
                <Select value={edit.sale_mode || "pdv"} onValueChange={(v) => setEdit((x) => ({ ...x, sale_mode: v }))}>
                  <SelectTrigger data-testid="user-sale-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdv" data-testid="sale-mode-pdv">Venda PDV</SelectItem>
                    <SelectItem value="ticket" data-testid="sale-mode-ticket">Venda ticket (venda direta)</SelectItem>
                    <SelectItem value="both" data-testid="sale-mode-both">Venda PDV e Venda ticket</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              {["ticket", "both"].includes(edit.sale_mode) && !isAdminRole(edit) && <TicketPayments edit={edit} setEdit={setEdit} />}
              <DirectSaleFields edit={edit} setEdit={setEdit} />
              <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-3 text-sm">
                <span>Garçom na comanda<span className="block text-xs text-muted-foreground">Mostra o garçom responsável (quem abriu) em cada comanda e libera o filtro "Minhas comandas" no app do celular</span></span>
                <Switch checked={!!edit.waiter_view} onCheckedChange={(v) => setEdit((x) => ({ ...x, waiter_view: v }))} data-testid="user-waiter-view-switch" />
              </label>
              <label className="flex items-center justify-between gap-3 rounded-lg border px-3 py-3 text-sm">
                <span>Comprovante de pagamento parcial<span className="block text-xs text-muted-foreground">No app do celular, depois de um pagamento parcial pergunta se quer imprimir o comprovante. Desligado: não pergunta e não imprime</span></span>
                <Switch checked={!!edit.partial_receipt} onCheckedChange={(v) => setEdit((x) => ({ ...x, partial_receipt: v }))} data-testid="user-partial-receipt-switch" />
              </label>
            </TabsContent>
          </Tabs>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fechar</Button>
          <Button onClick={save} disabled={busy} data-testid="save-user-btn">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salvar usuário</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
